import * as React from 'react';

import AdminUpgradeStatsContainer from 'admin/components/AdminUpgradeStats';
import AdminUpgradeFeatures from 'admin/components/AdminUpgradeFeatures/AdminUpgradeFeatures';

import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import { conditionallyAddPlus, getBlankQuote } from 'utils/selfServeHelpers';
import EventTracking from 'lib/EventTracking';
import {
  OrganizationType,
  UserDefinedOrganizationType,
  SelfServeHelpScreenSource,
  QuoteInput,
  LeadInput,
} from 'globalTypes';
import Row from 'components/Row';
import ToastActions from 'actions/ToastActions';
import {
  UpgradePlanData,
  UpgradePlanQuote,
} from 'admin/components/AdminUpgradePlan/AdminUpgradePlanContainer';
import AdminUpgradePlanOrgInfoSimple from 'admin/components/AdminUpgradePlan/AdminUpgradePlanOrgInfoSimple';
import AdminUpgradePlanOrgInfoInteractive from 'admin/components/AdminUpgradePlan/AdminUpgradePlanOrgInfoInteractive';
import { AdminUpgradePlanRequesterInfo } from 'admin/components/AdminUpgradePlan/AdminUpgradePlanRequesterInfo';
import {
  createSelfServeQuote,
  createSelfServeLeadWithoutQuote,
} from 'admin/actions/AdminUpgradePlanActions';
import { baseTrackingPrefix } from 'constants/SelfServeConstants';
import './styles/AdminUpgradePlanInfoInputForm.scss';
import moment from 'moment-timezone';

// fields that may be able to be edited by the user
// used to create subset of required fields depending on org info form type
type EditableFields = Pick<
  State,
  | 'country'
  | 'postalCode'
  | 'state'
  | 'city'
  | 'streetAddress'
  | 'organizationName'
  | 'orgType'
  | 'teachersCount'
  | 'studentsCount'
  | 'comment'
  | 'requesterFirstName'
  | 'requesterLastName'
  | 'requesterRole'
  | 'requesterPhone'
  | 'requesterEmail'
  | 'userDefinedOrgType'
>;

export type Props = {
  onPersistState: (fields: UpgradePlanData, quote: UpgradePlanQuote, onSuccess: Function) => void;
  onAdvance: () => void;
  onNoOrgStats: () => void;
  onSelectSchool?: (uuid: String) => void;
  initialOrgUuid?: string;
  displayOrgStats?: boolean;
  orgTypeString?: string;
  isSimpleForm?: boolean;
};

type State = {
  country?: string;
  state?: string;
  postalCode?: string;
  city?: string;
  streetAddress?: string;
  orgUuid?: string;
  organizationName?: string;
  orgType?: OrganizationType;
  lat?: number;
  long?: number;
  teachersCount?: number;
  studentsCount?: number;
  comment?: string;
  requesterFirstName?: string;
  requesterLastName?: string;
  requesterRole?: string;
  requesterPhone?: string;
  requesterEmail?: string;
  userDefinedOrgType?: UserDefinedOrganizationType;
  showAddressForm: boolean;
  errors: { [key: string]: string | undefined };
};

class AdminUpgradePlanInfoInputForm extends React.Component<Props, State> {
  readonly state: State;
  private readonly trackingPrefix = `${baseTrackingPrefix}.organization_info_page`;
  private readonly requiredSimpleFormFields: (keyof EditableFields)[] = [
    'studentsCount',
    'organizationName',
    'userDefinedOrgType',
    'country',
    'postalCode',
    'requesterFirstName',
    'requesterLastName',
    'requesterPhone',
    'requesterEmail',
  ];
  private readonly requiredInteractiveFormFields: (keyof EditableFields)[] = [
    'studentsCount',
    'teachersCount',
    'comment',
    'requesterFirstName',
    'requesterLastName',
    'requesterPhone',
    'requesterEmail',
    'requesterRole',
    'country',
    'postalCode',
    'state',
    'city',
    'streetAddress',
  ];
  private requiredFields: (keyof EditableFields)[];

  constructor(props: Props) {
    super(props);
    this.requiredFields = props.isSimpleForm
      ? this.requiredSimpleFormFields
      : this.requiredInteractiveFormFields;
    this.state = {
      orgUuid: props.initialOrgUuid,
      showAddressForm: !!props.initialOrgUuid,
      errors: {},
    };
  }

  canSubmit = () => {
    const requiredFieldValues = this.requiredFields.map(k => this.state[k]);
    const shouldSubmit =
      requiredFieldValues.every(v => Boolean(v)) && Object.keys(this.state.errors).length == 0;

    return shouldSubmit;
  };

  onSubmit = async () => {
    this.canSubmit() && this.submit();
  };

  doSimpleFormSubmit = async () => {
    const { onAdvance, onPersistState } = this.props;

    const inputs = this.createSelfServeLeadInputs();
    const response = await createSelfServeLeadWithoutQuote(inputs);

    if (response.success) {
      const { showAddressForm, ...upgradePlanData } = this.state;

      onPersistState(
        { ...upgradePlanData, purchaseSuccessful: false } as UpgradePlanData,
        getBlankQuote(),
        onAdvance
      );
    } else if (response.errorMessage) {
      ToastActions.showErrorToast(response.errorMessage);
    }
  };

  submit = async () => {
    const { onPersistState, onAdvance } = this.props;

    if (this.props.isSimpleForm) {
      return this.doSimpleFormSubmit();
    }

    // regular interactive form submit
    const inputs = this.createSelfServeQuoteInputs();
    const response = await createSelfServeQuote(inputs);

    if (response.success) {
      const { showAddressForm, ...upgradePlanData } = this.state;
      onPersistState(
        { ...upgradePlanData, purchaseSuccessful: false } as UpgradePlanData,
        response.quote,
        onAdvance
      );
    } else if (response.errorMessage) {
      ToastActions.showErrorToast(response.errorMessage);
    }
  };

  createSelfServeLeadInputs = (): LeadInput => {
    const {
      requesterFirstName,
      requesterLastName,
      requesterEmail,
      requesterPhone,
      userDefinedOrgType,
      organizationName,
      postalCode,
      country,
      studentsCount,
    } = this.state;

    return {
      requesterFirstName: requesterFirstName || '',
      requesterLastName: requesterLastName || '',
      requesterEmail: requesterEmail || '',
      requesterPhoneNumber: requesterPhone ? conditionallyAddPlus(requesterPhone) : '',
      orgType: userDefinedOrgType || UserDefinedOrganizationType.Other,
      orgName: organizationName || '',
      orgPostalCode: postalCode || '',
      orgCountry: country || '',
      studentsCount: studentsCount || 0,
      source: SelfServeHelpScreenSource.admin_contact,
      clientUuid: EventTracking.getClientId() || '',
    };
  };

  createSelfServeQuoteInputs = (): QuoteInput => {
    const {
      orgUuid,
      studentsCount,
      teachersCount,
      requesterFirstName,
      requesterLastName,
      requesterEmail,
      requesterPhone,
      requesterRole,
      lat,
      long,
      streetAddress,
      city,
      state,
      postalCode,
      country,
    } = this.state;

    // Automatically set the start date to one day after the day the
    // org info form is filled out and submitted
    const startDate = moment()
      .add(1, 'day')
      .toDate()
      .toISOString();

    return {
      organizationUuid: orgUuid || '',
      studentsCount: studentsCount || 0,
      teachersCount: teachersCount || 0,
      startDate: startDate,
      organizationAddress: {
        latitude: lat || null,
        longitude: long || null,
        streetAddress: streetAddress || '',
        city: city || '',
        state: state || '',
        country: country || '',
        postalCode: postalCode || '',
      },
      requesterFirstName: requesterFirstName,
      requesterLastName: requesterLastName,
      requesterEmail: requesterEmail || '',
      requesterPhoneNumber: requesterPhone ? conditionallyAddPlus(requesterPhone) : '',
      requesterRole,
      billingFirstName: requesterFirstName,
      billingLastName: requesterLastName,
      billingEmail: requesterEmail || '',
      billingPhoneNumber: requesterPhone ? conditionallyAddPlus(requesterPhone) : '',
      taxExempt: undefined, // Tax exemption status is determined in api
      lastScreenName: `org_info`,
    };
  };

  renderSidebar() {
    const { displayOrgStats, initialOrgUuid, onNoOrgStats } = this.props;

    return displayOrgStats && initialOrgUuid ? (
      <AdminUpgradeStatsContainer
        organizationUuid={initialOrgUuid}
        onNoStats={onNoOrgStats}
        data-test="upgrade-stats"
      />
    ) : (
      <AdminUpgradeFeatures data-test="upgrade-features" />
    );
  }

  handleChildUpdate = (
    stateChanges: {
      country?: string;
      postalCode?: string;
      state?: string;
      city?: string;
      streetAddress?: string;
      organizationName?: string;
      orgType?: OrganizationType;
      teachersCount?: number;
      studentsCount?: number;
      comment?: string;
      requesterFirstName?: string;
      requesterLastName?: string;
      requesterRole?: string;
      requesterPhone?: string;
      requesterEmail?: string;
      userDefinedOrgType?: UserDefinedOrganizationType;
    },
    badPostalCodeCountryCombo?: boolean
  ) => {
    const { postal_code, ...errors } = this.state.errors;
    if (Boolean(badPostalCodeCountryCombo)) {
      errors.postal_code = 'Invalid postal code';
    }

    this.setState({ ...stateChanges, errors });
  };

  renderOrgInfoSection = () => {
    if (this.props.isSimpleForm) {
      return <AdminUpgradePlanOrgInfoSimple onUpdate={this.handleChildUpdate} />;
    } else {
      return (
        <AdminUpgradePlanOrgInfoInteractive
          country={this.state.country}
          state={this.state.state}
          postalCode={this.state.postalCode}
          city={this.state.city}
          streetAddress={this.state.streetAddress}
          organizationName={this.state.organizationName}
          onUpdate={this.handleChildUpdate}
        />
      );
    }
  };

  renderRequesterSection = () => {
    const showRole = !Boolean(this.props.isSimpleForm);

    return (
      <AdminUpgradePlanRequesterInfo
        requesterFirstName={this.state.requesterFirstName}
        requesterLastName={this.state.requesterLastName}
        requesterEmail={this.state.requesterEmail}
        requesterPhone={this.state.requesterPhone}
        requesterRole={this.state.requesterRole}
        showRole={showRole}
        onUpdate={this.handleChildUpdate}
      />
    );
  };
  render() {
    return (
      <div className="AdminUpgradePlanInfoInputForm">
        <div className="AdminUpgradePlanInfoInputForm-content">
          <div className="AdminUpgradePlanInfoInputForm-content-body">
            <div className="AdminUpgradePlanInfoInputForm-content-body-forms">
              {this.renderOrgInfoSection()}
              {this.renderRequesterSection()}
              <Columns2>
                <Button
                  disabled={!this.canSubmit()}
                  onClick={this.onSubmit}
                  data-test="submit-button">
                  Submit
                </Button>
              </Columns2>
            </div>
            <div className="AdminUpgradePlanInfoInputForm-content-body--desktop-side">
              <Row size="xxxlarge">{this.renderSidebar()}</Row>
            </div>
          </div>
        </div>
        <div className="AdminUpgradePlanInfoInputForm--mobile-bottom">
          <div className="AdminUpgradePlanInfoInputForm-info-container">{this.renderSidebar()}</div>
        </div>
      </div>
    );
  }
}

export { AdminUpgradePlanInfoInputForm };
export default AdminUpgradePlanInfoInputForm;
